import { MapPin } from "lucide-react";
import { ACADEMY, hasAddress, mapsEmbedUrl } from "@/config/academy";

const VENUE_IMAGE =
  "https://images.pexels.com/photos/4815483/pexels-photo-4815483.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Trophy wall: real photos shared by the owner, captions describe only what
// is visible — no invented results.
const TROPHY_PHOTOS = [
  {
    src: "/images/tournament-trophies.jpeg",
    alt: "Student competing on the tournament floor with championship trophies on display",
    caption: "Tournament floor &middot; trophies at stake",
    testId: "trophy-photo-1",
  },
  {
    src: "/images/coach-gaurav.jpeg",
    alt: "Coach Gaurav Gautam competing at a FIDE-rated rapid and blitz event",
    caption: "FIDE-rated rapid &amp; blitz &middot; Khelo Chess India event",
    testId: "trophy-photo-2",
  },
];

const TrophyWall = () => (
  <div className="mt-12" data-testid="trophy-wall">
    <div className="flex flex-col md:flex-row md:items-end justify-between mb-6">
      <h3 className="font-display text-2xl uppercase tracking-tight text-emerald-950">From the Tournament Floor</h3>
      <p className="text-xs font-mono text-stone-500 mt-2 md:mt-0 max-w-sm">
        Real frames from rated events our players compete in.
      </p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {TROPHY_PHOTOS.map((photo) => (
        <figure
          key={photo.src}
          data-testid={photo.testId}
          className="rounded-lg overflow-hidden border border-stone-300 bg-white shadow-sm"
        >
          <div className="aspect-[4/3] overflow-hidden">
            <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <figcaption
            className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-stone-600"
            dangerouslySetInnerHTML={{ __html: photo.caption }}
          />
        </figure>
      ))}
    </div>
  </div>
);

// Address + map strip: the map and address unlock as soon as the owner
// shares the academy's street address (see src/config/academy.js).
const AddressMap = () => (
  <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="venue-address-map">
    <div className="p-6 bg-white rounded border border-stone-300 flex flex-col justify-between">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-emerald-800 mb-2">Visit Us</div>
        {hasAddress ? (
          <p className="text-sm text-stone-800 leading-relaxed" data-testid="venue-address-text">
            {ACADEMY.address}
          </p>
        ) : (
          <p className="text-sm text-stone-500 leading-relaxed" data-testid="venue-address-text">
            Address to be confirmed &mdash; share the academy street address and it will appear here
            with a live map.
          </p>
        )}
      </div>
      <div className="mt-6 pt-4 border-t border-stone-200 font-mono text-xs text-stone-500">
        Physical campus &amp; worldwide online batches
      </div>
    </div>

    <div
      className="rounded-lg overflow-hidden border border-stone-300 min-h-[240px] bg-white flex items-center justify-center"
      data-testid="venue-map"
    >
      {hasAddress ? (
        <iframe
          title="Academy location map"
          src={mapsEmbedUrl}
          className="w-full h-full min-h-[240px] border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="text-center p-8">
          <MapPin className="w-8 h-8 text-emerald-900 mx-auto" strokeWidth={1.5} />
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mt-3">
            Map unlocks once the address is added
          </p>
        </div>
      )}
    </div>
  </div>
);

export const Venue = () => {
  return (
    <section id="medals" className="py-20 border-b border-stone-300 bg-[#faf9f5]" data-testid="venue-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-emerald-800 mb-2">Chapter 04</div>
            <h2 className="font-display text-3xl uppercase tracking-tight text-emerald-950">Medal Honors &amp; Location</h2>
            <p className="text-sm text-stone-600 mt-2" data-testid="venue-location-detail">
              Physical academy training center with dedicated tournament boards, plus live interactive
              classes open to students worldwide.
            </p>

            <div className="mt-8 space-y-4">
              <div className="p-4 bg-white rounded border border-stone-300" data-testid="venue-physical-card">
                <div className="font-mono text-xs text-emerald-800 font-bold uppercase">Physical Academy</div>
                <p className="text-xs text-stone-600 mt-1">
                  One central facility equipped with DGT electronic boards and classical tournament library.
                </p>
              </div>
              <div className="p-4 bg-white rounded border border-stone-300" data-testid="venue-online-card">
                <div className="font-mono text-xs text-emerald-800 font-bold uppercase">Global Online Batches</div>
                <p className="text-xs text-stone-600 mt-1">
                  Live screen analysis and interactive tactical puzzles accessible anywhere.
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-stone-300 shadow-md" data-testid="venue-image">
              <img
                src={VENUE_IMAGE}
                alt="Students playing tournament chess at the academy hall"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/70 via-transparent to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <span className="px-2.5 py-1 bg-emerald-950 text-white text-[11px] font-mono uppercase rounded">Academy Center</span>
                  <span className="text-xs font-mono text-emerald-950 bg-white/90 px-2 py-1 rounded">Physical &amp; Online</span>
                </div>
                <div className="bg-white/95 p-4 rounded border border-stone-200">
                  <p className="font-mono text-[10px] text-stone-500 uppercase">Training Venue</p>
                  <p className="font-bold text-stone-900 text-sm mt-0.5">Chaya Pre School, PJR Colony, Kondapur</p>
                  <p className="text-xs text-stone-600 mt-0.5">Medalists trained across district &amp; state brackets.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <TrophyWall />

        <AddressMap />
      </div>
    </section>
  );
};
